import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Button,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';

const EmailTemplateManager = ({ open, onClose, templates, onSaveTemplates }) => {
  const [templateList, setTemplateList] = useState(templates);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const handleAddNew = () => {
    setEditingTemplate({
      id: Date.now(),
      name: '',
      subject: '',
      content: ''
    });
    setIsEditing(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate({ ...template });
    setIsEditing(true);
  };

  const handleDelete = (templateId) => {
    const newList = templateList.filter(t => t.id !== templateId);
    setTemplateList(newList);
    onSaveTemplates(newList);
  };

  const handleCancel = () => {
    setEditingTemplate(null);
    setIsEditing(false);
  };

  const handleSave = () => {
    if (!editingTemplate.name || !editingTemplate.subject) return;

    const newList = templateList.filter(t => t.id !== editingTemplate.id);
    const updatedList = [...newList, editingTemplate];
    setTemplateList(updatedList);
    onSaveTemplates(updatedList);
    setIsEditing(false);
    setEditingTemplate(null);
  };

  return (
    <Dialog 
      open={open} 
      onClose={isEditing ? undefined : onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid #e0e0e0',
        p: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6">Email Templates</Typography>
        {!isEditing && (
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {!isEditing ? (
          <>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 4,
              mt: 3,
              px: 2
            }}>
              <Typography 
                variant="subtitle1"
                sx={{
                  fontWeight: 500,
                  color: 'text.primary',
                  fontSize: '1.1rem'
                }}
              >
                Available Templates ({templateList.length})
              </Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddNew}
                variant="contained"
                size="large"
              >
                Create New Template
              </Button>
            </Box>

            <List>
              {templateList.map((template) => (
                <Paper 
                  key={template.id}
                  sx={{ 
                    mb: 2,
                    p: 2,
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        {template.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {template.subject}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEdit(template)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDelete(template.id)}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              ))}
            </List>
          </>
        ) : (
          <Box>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                mb: 4,
                mt: 2,
                px: 1
              }}
            >
              {editingTemplate.id 
                ? 'Make changes to your email template below'
                : 'Create a new email template by filling out the form below'
              }
            </Typography>
            
            <TextField
              fullWidth
              label="Template Name"
              value={editingTemplate.name}
              onChange={(e) => setEditingTemplate(prev => ({
                ...prev,
                name: e.target.value
              }))}
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Subject"
              value={editingTemplate.subject}
              onChange={(e) => setEditingTemplate(prev => ({
                ...prev,
                subject: e.target.value
              }))}
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="Content"
              multiline
              rows={12}
              value={editingTemplate.content}
              onChange={(e) => setEditingTemplate(prev => ({
                ...prev,
                content: e.target.value
              }))}
              sx={{ mb: 3 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!editingTemplate.name || !editingTemplate.subject}
              >
                Save Template
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EmailTemplateManager; 